import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  useVendor, 
  useVerifyVendor, 
  useRejectVendor,
  useUpdateVendorProfile,
  useSuspendVendor
} from '../../services/vendorService';
import VendorHeader from '../../components/admin/VendorHeader';
import OverviewTab from '../../components/admin/vendor/OverviewTab';
import ServicesCategoryTab from '../../components/admin/vendor/ServicesCategoryTab';
import ServicesTab from '../../components/admin/vendor/ServicesTab';
import PackagesTab from '../../components/admin/vendor/PackagesTab';
import PortfolioTab from '../../components/admin/vendor/PortfolioTab';
import VerificationTab from '../../components/admin/vendor/VerificationTab';
import AnalyticsTab from '../../components/admin/vendor/AnalyticsTab';
import {
  ArrowLeft,
  XCircle,
  Star,
  Calendar,
  Users,
  Camera,
  Shield,
  DollarSign,
  FileText,
  Eye,
  Package,
  Gift
} from 'lucide-react';
import { SearchSelect, Button } from '../../components/shared';
import {  StatsGrid } from '../../components/admin';
import { useToastContext } from '../../contexts/ToastContext';

const AdminVendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Utility function for readable date formatting
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const { success, error: showError } = useToastContext();
  const [verificationData, setVerificationData] = useState({
    verification_level: 'verified',
    verification_notes: ''
  });
  const [rejectionData, setRejectionData] = useState({
    rejection_reason: '',
    rejection_type: 'other'
  });
  // React Query hooks
  const { 
    data: vendorData, 
    isLoading: loading, 
    error: vendorError,
    refetch: refetchVendor
  } = useVendor(id);

  // console.log(vendorData, 'vendorData');
  
  // Mutations
  const verifyVendorMutation = useVerifyVendor({
    onSuccess: () => {
      success('Vendor verified successfully!');
      setShowVerificationModal(false);
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to verify vendor');
    }
  });

  const rejectVendorMutation = useRejectVendor({
    onSuccess: () => {
      success('Vendor rejected successfully!');
      setShowRejectionModal(false);
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to reject vendor');
    }
  });

  const updateVendorProfileMutation = useUpdateVendorProfile({
    onSuccess: () => {
      success('Vendor profile updated successfully!');
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update vendor profile');
    }
  });

  const suspendVendorMutation = useSuspendVendor({
    onSuccess: (_, variables) => {
      const msg = variables.isActive ? 'Vendor reactivated successfully! They have been notified by email.' : 'Vendor suspended. They have been logged out and notified by email.';
      success(msg);
      setShowSuspendModal(false);
      setSuspendReason('');
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update vendor status');
    }
  });

  // Extract data
  const vendor = vendorData?.data;
  const vendorProfile = vendor?.vendor_profile;
  const vendorSubscriptions = useMemo(() => vendor?.vendor_subscriptions || [], [vendor?.vendor_subscriptions]);
  const userVerifications = vendor?.user_verifications || [];
  const error = vendorError?.message || null;

  // Handle vendor loading errors (omit showError from deps: ToastProvider passes a new function each render)
  useEffect(() => {
    if (!vendorError) return;
    const msg =
      vendorError?.response?.data?.message ||
      vendorError?.message ||
      'Failed to load vendor details';
    showError(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable toast once per vendorError
  }, [vendorError]);

  // Handle verification
  const handleVerify = () => {
    verifyVendorMutation.mutate({
      id,
      data: verificationData
    });
  };

  // Handle rejection
  const handleReject = () => {
    rejectVendorMutation.mutate({
      id,
      data: rejectionData
    });
  };

  // Handle featured status toggle
  const handleToggleFeatured = () => {
    updateVendorProfileMutation.mutate({
      id,
      data: {
        is_featured: !vendorProfile?.is_featured
      }
    });
  };

  // Handle active status toggle
  const handleToggleActive = () => {
    updateVendorProfileMutation.mutate({
      id,
      data: {
        is_active: !vendor?.is_active
      }
    });
  };

  const handleSuspend = () => {
    const isActive = vendor?.is_active !== false && vendorProfile?.is_active !== false;
    suspendVendorMutation.mutate({
      id,
      isActive: !isActive,
      reason: isActive ? suspendReason : null
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading vendor details...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error || 'Vendor not found'}</p>
          <button
            onClick={() => navigate('/admin/vendors')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Vendors
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'services', label: 'Services', icon: Package },
    { id: 'packages', label: 'Packages', icon: Gift },
    { id: 'services_category', label: 'Categories', icon: Camera },
    { id: 'portfolio', label: 'Portfolio', icon: FileText },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'analytics', label: 'Analytics', icon: DollarSign }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-4">
          <VendorHeader
            vendor={vendor}
            vendorProfile={vendorProfile}
            onVerify={() => setShowVerificationModal(true)}
            onReject={() => setShowRejectionModal(true)}
            onSuspend={() => setShowSuspendModal(true)}
          />
        </div>

        {/* Vendor Status Cards */}
        <div className="mb-6">
          <StatsGrid 
            stats={[
              {
                key: 'verification',
                title: 'Verification',
                value: vendorProfile?.is_verified ? 'Verified' : 'Pending',
                icon: Shield,
                variant: vendorProfile?.is_verified ? 'success' : 'warning'
              },
              {
                key: 'rating',
                title: 'Rating',
                value: vendorProfile?.average_rating ? `${vendorProfile.average_rating}/5.0` : 'N/A',
                icon: Star,
                variant: 'default'
              },
              {
                key: 'services',
                title: 'Categories',
                value: vendorProfile?.vendor_categories?.length || 0,
                icon: Users,
                variant: 'info'
              },
              {
                key: 'joined',
                title: 'Joined',
                value: formatDate(vendor?.created_at),
                icon: Calendar,
                variant: 'default',
                className: 'whitespace-nowrap'
              }
            ]}
            columns={4}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex overflow-x-auto scrollbar-hide px-2 sm:px-6">
              <div className="flex space-x-2 sm:space-x-8 min-w-max">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const shortLabel = tab.label === 'Verification' ? 'Verify' : 
                                   tab.label === 'Analytics' ? 'Stats' :
                                   tab.label === 'Services Category' ? 'Categories' :
                                   tab.label.split(' ')[0];
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          <div className="p-2 xs:p-3 sm:p-4 lg:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <OverviewTab
                vendor={vendor}
                vendorProfile={vendorProfile}
                vendorSubscriptions={vendorSubscriptions}
                formatDate={formatDate}
                onToggleFeatured={handleToggleFeatured}
                onToggleActive={handleToggleActive}
                isUpdatingFeatured={updateVendorProfileMutation.isPending}
                isUpdatingActive={updateVendorProfileMutation.isPending}
              />
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <ServicesTab vendorProfile={vendorProfile} />
            )}

            {/* Packages Tab */}
            {activeTab === 'packages' && (
              <PackagesTab vendorProfile={vendorProfile} onUpdate={refetchVendor} />
            )}

            {/* Services Category Tab */}
            {activeTab === 'services_category' && (
              <ServicesCategoryTab vendorProfile={vendorProfile} />
            )}

            {/* Portfolio Tab */}
            {activeTab === 'portfolio' && (
              <PortfolioTab vendorProfile={vendorProfile} />
            )}

            {/* Verification Tab */}
            {activeTab === 'verification' && (
              <VerificationTab
                vendor={vendor}
                vendorProfile={vendorProfile}
                userVerifications={userVerifications}
                formatDate={formatDate}
              />
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <AnalyticsTab vendor={vendor} vendorProfile={vendorProfile} />
            )}
          </div>
        </div>

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Verify Vendor</h3>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Notes
                  </label>
                  <textarea
                    value={verificationData.verification_notes}
                    onChange={(e) => setVerificationData({...verificationData, verification_notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add verification notes..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => setShowVerificationModal(false)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleVerify}
                    disabled={verifyVendorMutation.isPending}
                    loading={verifyVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-green-600 text-green-600 hover:bg-green-50"
                  >
                    {verifyVendorMutation.isPending ? 'Verifying...' : 'Verify Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? 'Suspend Vendor' : 'Reactivate Vendor'}
                </h3>
                {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      The vendor will be logged out immediately and cannot log in until reactivated. An email notification will be sent.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                      <textarea
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Reason for suspension (will be included in email)..."
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 mb-6">
                    The vendor will be able to log in and access their account again.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={suspendVendorMutation.isPending}
                    loading={suspendVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className={`w-full sm:w-auto rounded-full ${
                      (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'border-amber-600 text-amber-600 hover:bg-amber-50'
                        : 'border-green-600 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {suspendVendorMutation.isPending
                      ? 'Processing...'
                      : (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'Suspend Vendor'
                        : 'Reactivate Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? 'Suspend Vendor' : 'Reactivate Vendor'}
                </h3>
                {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      The vendor will be logged out immediately and cannot log in until reactivated. An email notification will be sent.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                      <textarea
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Reason for suspension (will be included in email)..."
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 mb-6">
                    The vendor will be able to log in and access their account again.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={suspendVendorMutation.isPending}
                    loading={suspendVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className={`w-full sm:w-auto rounded-full ${
                      (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'border-amber-600 text-amber-600 hover:bg-amber-50'
                        : 'border-green-600 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {suspendVendorMutation.isPending
                      ? 'Processing...'
                      : (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'Suspend Vendor'
                        : 'Reactivate Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Vendor</h3>
                
                <div className="mb-4">
                  <SearchSelect
                    label="Rejection Type"
                    value={rejectionData.rejection_type}
                    onChange={(value) => setRejectionData({...rejectionData, rejection_type: value})}
                    options={[
                      { value: 'document_issue', label: 'Document Issue' },
                      { value: 'background_check', label: 'Background Check' },
                      { value: 'other', label: 'Other' }
                    ]}
                    placeholder="Select rejection type..."
                    required={true}
                    size="sm"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectionData.rejection_reason}
                    onChange={(e) => setRejectionData({...rejectionData, rejection_reason: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Explain why the vendor is being rejected..."
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => setShowRejectionModal(false)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={rejectVendorMutation.isPending || !rejectionData.rejection_reason}
                    loading={rejectVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-red-600 text-red-600 hover:bg-red-50"
                  >
                    {rejectVendorMutation.isPending ? 'Rejecting...' : 'Reject Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVendorDetails;
